import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import authService from "../services/authService";

const AuthContext = createContext();

const isErpPath = (pathname) => pathname.startsWith("/erp/");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const initRef = useRef(0);

  const initializeForPath = useCallback(async (pathname) => {
    const requestId = ++initRef.current;

    try {
      setAuthError(null);

      if (!isErpPath(pathname)) {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      const sessionResult = await authService.getSession();
      if (requestId !== initRef.current) return;

      if (sessionResult?.success && sessionResult?.data?.session?.user) {
        const authUser = sessionResult.data.session.user;
        setUser(authUser);

        const profileResult = await authService.getUserProfile(authUser.id);
        if (requestId !== initRef.current) return;

        if (profileResult?.success) {
          setUserProfile(profileResult.data);
        } else {
          setAuthError(profileResult?.error || "Failed to load user profile");
          setUser(null);
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
    } catch (error) {
      if (requestId !== initRef.current) return;
      console.log("Auth initialization error:", error);
      setUser(null);
      setUserProfile(null);
    } finally {
      if (requestId === initRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } = {} } = authService.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      setAuthError(null);

      if (event === "SIGNED_IN" && session?.user) {
        setUser(session.user);

        authService.getUserProfile(session.user.id).then((profileResult) => {
          if (profileResult?.success && isMounted) {
            setUserProfile(profileResult.data);
          } else if (isMounted) {
            setAuthError(profileResult?.error || "Failed to load user profile");
            setUser(null);
            setUserProfile(null);
          }
        });
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setUserProfile(null);
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  const signIn = async (email, password) => {
    try {
      setAuthError(null);

      const result = await authService.signIn(email, password);

      if (!result?.success) {
        const errorMsg = result?.error || "Login failed";
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      if (result.data?.session?.user && result.userProfile) {
        setUser(result.data.session.user);
        setUserProfile(result.userProfile);
      }

      return {
        success: true,
        data: result.data,
        userProfile: result.userProfile,
        dashboardRoute: result.dashboardRoute,
      };
    } catch (error) {
      const errorMsg = "Something went wrong during login. Please try again.";
      setAuthError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const signUp = async (email, password, userData = {}) => {
    setAuthError(null);
    return await authService.signUp(email, password, userData);
  };

  const signOut = async () => {
    try {
      setAuthError(null);
      await authService.signOut();
      setUser(null);
      setUserProfile(null);
      return { success: true };
    } catch (error) {
      setUser(null);
      setUserProfile(null);
      return { success: true };
    }
  };

  const updateProfile = async () => {
    try {
      setAuthError(null);
      return { success: true };
    } catch (error) {
      const errorMsg = "Failed to update profile";
      setAuthError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const resetPassword = async (email) => {
    try {
      setAuthError(null);

      const result = await authService.resetPassword(email);

      if (!result?.success) {
        setAuthError(result?.error || "Password reset failed");
        return { success: false, error: result?.error };
      }

      return { success: true };
    } catch (error) {
      const errorMsg = "Something went wrong sending reset email. Please try again.";
      setAuthError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    authError,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    initializeForPath,
    getDemoCredentials: authService.getDemoCredentials,
    clearError: () => setAuthError(null),
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Must render inside <Router> — syncs ERP auth with the current route */
export function AuthRouteSync() {
  const location = useLocation();
  const { initializeForPath } = useAuth();

  useEffect(() => {
    initializeForPath(location.pathname);
  }, [location.pathname, initializeForPath]);

  return null;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
